import React, { useEffect, useState } from 'react';
import {
    fetchMyUplinkSystems, fetchMyUplinkEndpoints, queryMyUplinkEndpoint, MyUplinkEndpointDef,
} from '../../services/api';
import { MyUplinkSystem } from '../../types';
import { ui } from '../../styles/shared';
import DisabledNotice from './DisabledNotice';

interface Props {
    enabled: boolean;
    onError: (msg: string) => void;
    onGoToStatus: () => void;
}

// Flik: kör valfri myUplink-adress och visa svaret.
const ApiExplorerTab: React.FC<Props> = ({ enabled, onError, onGoToStatus }) => {
    const [systems, setSystems] = useState<MyUplinkSystem[]>([]);
    const [endpoints, setEndpoints] = useState<MyUplinkEndpointDef[]>([]);
    const [key, setKey] = useState('');
    const [params, setParams] = useState<Record<string, string>>({});
    const [result, setResult] = useState<unknown>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Hämta adresser och system när fliken öppnas
    useEffect(() => {
        if (!enabled) return;
        fetchMyUplinkEndpoints().then(e => {
            setEndpoints(e);
            if (e.length > 0) {
                setKey(e[0].key);
                setParams({});
            }
        });
        fetchMyUplinkSystems()
            .then(s => setSystems(s))
            .catch(e => onError('Kunde inte hämta systemen: ' + e.message));
    }, [enabled, onError]);

    const selectedEndpoint = endpoints.find(e => e.key === key) ?? null;

    // Byte av adress nollställer parametrar och svar
    const handleKeyChange = (k: string) => {
        setKey(k);
        setParams({});
        setResult(null);
        setError(null);
    };

    const handleRun = async () => {
        if (!selectedEndpoint) return;
        // Fyll i systemId/deviceId från laddade system om de saknas
        const p = { ...params };
        if (selectedEndpoint.pathParams.includes('systemId') && !p.systemId && systems.length > 0)
            p.systemId = systems[0].systemId ?? (systems[0] as any).id ?? '';
        if (selectedEndpoint.pathParams.includes('deviceId') && !p.deviceId && systems.length > 0)
            p.deviceId = systems[0].devices[0]?.id ?? '';
        setLoading(true);
        setError(null);
        try {
            setResult(await queryMyUplinkEndpoint(key, p));
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    if (!enabled) return <DisabledNotice onGoToStatus={onGoToStatus} />;

    return (
        <div>
            {/* Val av adress och parametrar */}
            <div style={ui.card}>
                <h3 style={{ marginTop: 0, marginBottom: '16px' }}>🔍 myUplink API-utforskare</h3>
                <p style={{ color: '#666', fontSize: '13px', marginTop: 0 }}>
                    Nya adresser läggs till i <code>MyUplinkService.ts → MYUPLINK_ENDPOINTS</code> — inget behöver ändras i gränssnittet.
                </p>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <div style={{ flex: '1', minWidth: '220px' }}>
                        <label style={ui.label}>Adress</label>
                        <select style={ui.input} value={key} onChange={e => handleKeyChange(e.target.value)}>
                            {endpoints.map(ep => (
                                <option key={ep.key} value={ep.key}>{ep.label}</option>
                            ))}
                        </select>
                    </div>
                    {/* Ett fält per parameter som adressen behöver */}
                    {selectedEndpoint?.pathParams.map(p => {
                        // Föreslå värden från laddade system
                        const autoOptions: { value: string; label: string }[] = [];
                        if (p === 'systemId') {
                            systems.forEach(s => autoOptions.push({ value: s.systemId ?? (s as any).id, label: s.name }));
                        } else if (p === 'deviceId') {
                            systems.forEach(s => s.devices.forEach(d => autoOptions.push({ value: d.id, label: `${s.name} — ${d.productName || d.serialNumber}` })));
                        }
                        return (
                            <div key={p} style={{ flex: '1', minWidth: '200px' }}>
                                <label style={ui.label}>{p}</label>
                                {autoOptions.length > 0 ? (
                                    <select style={ui.input}
                                        value={params[p] ?? (autoOptions[0]?.value ?? '')}
                                        onChange={e => setParams(prev => ({ ...prev, [p]: e.target.value }))}>
                                        {autoOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                ) : (
                                    <input style={ui.input} placeholder={p}
                                        value={params[p] ?? ''}
                                        onChange={e => setParams(prev => ({ ...prev, [p]: e.target.value }))} />
                                )}
                            </div>
                        );
                    })}
                    <button onClick={handleRun} disabled={loading || !selectedEndpoint}
                        style={ui.btn(loading ? '#aaa' : '#007bff')}>
                        {loading ? 'Hämtar…' : '▶ Kör'}
                    </button>
                </div>
                {selectedEndpoint && (
                    <div style={{ fontSize: '12px', color: '#888' }}>
                        <code style={{ backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '3px' }}>{selectedEndpoint.path}</code>
                        {' — '}{selectedEndpoint.description}
                    </div>
                )}
            </div>

            {/* Svar */}
            {error && (
                <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '12px 16px', borderRadius: '4px', marginBottom: '16px' }}>
                    {error}
                </div>
            )}
            {result !== null && !loading && (
                <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #ddd', overflow: 'hidden' }}>
                    <ExplorerResult data={result} />
                </div>
            )}
        </div>
    );
};

// ── Visar svaret som tabell, lista eller JSON ────────────────────────────────

const ExplorerResult: React.FC<{ data: unknown }> = ({ data }) => {
    // Packa upp vanliga sidhöljen: { content: [...] } eller { systems: [...] }
    const unwrap = (d: unknown): unknown => {
        if (d && typeof d === 'object' && !Array.isArray(d)) {
            const obj = d as Record<string, unknown>;
            if (Array.isArray(obj.content)) return obj.content;
            if (Array.isArray(obj.systems)) return obj.systems;
        }
        return d;
    };
    const display = unwrap(data);

    if (Array.isArray(display) && display.length > 0 && typeof display[0] === 'object') {
        // Lista med objekt → tabell
        const cols = Array.from(new Set(display.flatMap(row => Object.keys(row as object))));
        return (
            <div style={{ overflowX: 'auto' }}>
                <div style={{ padding: '10px 14px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd', fontSize: '12px', color: '#888' }}>
                    {display.length} {display.length === 1 ? 'post' : 'poster'}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                            {cols.map(c => <th key={c} style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#555', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{c}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {(display as Record<string, unknown>[]).map((row, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                                {cols.map(c => (
                                    <td key={c} style={{ padding: '7px 12px', verticalAlign: 'top', maxWidth: '320px', wordBreak: 'break-word' }}>
                                        {renderCell(row[c])}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    if (display && typeof display === 'object' && !Array.isArray(display)) {
        // Ett objekt → nyckel/värde-lista
        const entries = Object.entries(display as object);
        return (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <tbody>
                    {entries.map(([k, v], i) => (
                        <tr key={k} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                            <td style={{ padding: '8px 14px', fontWeight: 'bold', color: '#555', width: '35%', verticalAlign: 'top', fontFamily: 'monospace', fontSize: '12px' }}>{k}</td>
                            <td style={{ padding: '8px 14px', wordBreak: 'break-word' }}>{renderCell(v)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    // Annars: visa som JSON
    return (
        <pre style={{ margin: 0, padding: '16px', fontSize: '12px', overflowX: 'auto', backgroundColor: '#1e1e1e', color: '#d4d4d4' }}>
            {JSON.stringify(data, null, 2)}
        </pre>
    );
};

function renderCell(value: unknown): React.ReactNode {
    if (value === null || value === undefined) return <span style={{ color: '#ccc' }}>—</span>;
    if (typeof value === 'boolean') return <span style={{ color: value ? '#28a745' : '#dc3545' }}>{value ? 'ja' : 'nej'}</span>;
    if (typeof value === 'object') return <span style={{ color: '#888', fontSize: '11px' }}>{JSON.stringify(value)}</span>;
    return String(value);
}

export default ApiExplorerTab;
